import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Skeleton,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Badge,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarningIcon from "@mui/icons-material/Warning";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SortIcon from "@mui/icons-material/Sort";
import { GetAnalystData } from "../../../services/DashboardService";
import "./Panels.css";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import AnalystSummaryPanel from "./AnalystSummaryPanel"; // 👈 import new component

export default function AnalystPanel({ startDate, endDate, selectedUser }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("dueForAnalyst");
  const [order, setOrder] = useState("asc");
  const [viewMode, setViewMode] = useState("table"); // "table" | "summary"

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetAnalystData(startDate, endDate, selectedUser);
      setData(res || []);
      setLastUpdated(new Date());
      setPage(1);
    } catch (e) {
      setData([]);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
      case "urgent":
        return "#f44336";
      case "medium":
        return "#ff9800";
      default:
        return "#4caf50";
    }
  };

  const isOverdue = (dueDate) => dueDate && new Date(dueDate) < new Date();
  const isReportOverdue = (dueDate) =>
    dueDate && new Date(dueDate) < new Date();

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "2-digit",
    });
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter((row) => {
      const s = searchTerm.toLowerCase();
      return (
        row.clientName?.toLowerCase().includes(s) ||
        row.orderNumber?.toString().includes(s) ||
        row.labId?.toLowerCase().includes(s) ||
        row.parameter?.toLowerCase().includes(s) ||
        row.methodCode?.toLowerCase().includes(s) ||
        row.analyst?.toLowerCase().includes(s) ||
        row.numericVal6?.toLowerCase().includes(s) ||
        row.turnAroundTime?.toLowerCase().includes(s)
      );
    });

    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      if (["dueForAnalyst", "reportDueDate"].includes(orderBy)) {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }
      if (["orderNumber", "daysInHouse"].includes(orderBy)) {
        aValue = aValue || 0;
        bValue = bValue || 0;
      }
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || "";
      }
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data, searchTerm, orderBy, order]);

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredAndSortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedData, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);

  const overdueCount = data.filter((row) =>
    isOverdue(row.dueForAnalyst)
  ).length;
  const reportOverdueCount = data.filter((row) =>
    isReportOverdue(row.reportDueDate)
  ).length;

  if (loading) {
    return (
      <Box className="panel-loading">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box className="modern-panel">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }} // 👈 align top on mobile
        flexDirection={{ xs: "column", sm: "row" }} // 👈 stack on mobile
        gap={1}
        mb={2}
      >
        {/* Left side */}
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="h6">Analyst Workload</Typography>
          <Chip
            label={`${data.length} Pending Samples`}
            icon={<AssignmentIcon />}
            size="small"
          />
          {overdueCount > 0 && (
            <Badge badgeContent={overdueCount} color="error">
              <Chip
                label="Analysis Overdue"
                icon={<WarningIcon />}
                size="small"
                color="error"
              />
            </Badge>
          )}
          {reportOverdueCount > 0 && (
            <Badge badgeContent={reportOverdueCount} color="warning">
              <Chip
                label="Report Overdue"
                icon={<AccessTimeIcon />}
                size="small"
                color="warning"
              />
            </Badge>
          )}
        </Box>

        {/* Right side */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          flexWrap="wrap" // 👈 prevent squish
          justifyContent={{ xs: "flex-start", sm: "flex-end" }} // 👈 align properly
          width={{ xs: "100%", sm: "auto" }} // 👈 full width on mobile
        >
          {lastUpdated && (
            <Typography variant="caption" color="primary">
              Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <IconButton onClick={loadData} disabled={loading} size="small">
            <RefreshIcon sx={{ color: "#007DD1" }} />
          </IconButton>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newValue) => {
              if (newValue) setViewMode(newValue);
            }}
            size="small"
          >
            <ToggleButton value="table">Table</ToggleButton>
            <ToggleButton value="summary">Summary</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        className="panel-subtitle"
        mb={2}
      >
        Samples assigned to {selectedUser} and their pending workload
      </Typography>

      {viewMode === "summary" ? (
        <AnalystSummaryPanel data={filteredAndSortedData} />
      ) : (
        <>
          {/* Controls */}
          <Box mb={2} display="flex" gap={2}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small">
              <InputLabel>Sort by</InputLabel>
              <Select
                value={orderBy}
                onChange={(e) => handleSort(e.target.value)}
                label="Sort by"
              >
                <MenuItem value="dueForAnalyst">Analysis Due</MenuItem>
                <MenuItem value="reportDueDate">Report Due</MenuItem>
                <MenuItem value="clientName">Client</MenuItem>
                <MenuItem value="orderNumber">Order #</MenuItem>
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="daysInHouse">Days in House</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Per Page</InputLabel>
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(e.target.value);
                  setPage(1);
                }}
                label="Per Page"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Lab ID</TableCell>
                  <TableCell>Sample #</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Parameter</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Analysis Due</TableCell>
                  <TableCell>Report Due</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>TAT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      {row.orderNumber ? (
                        <a
                          href={`/vlp/edit/lab-orders/${row.orderNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1976d2",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {row.orderNumber}
                        </a>
                      ) : (
                        "-"
                      )}
                    </TableCell>

                    <TableCell>{row.labId || "-"}</TableCell>
                    {/* <TableCell>{row.sampleNumber || "-"}</TableCell> */}
                    <TableCell>
                      {row.sampleNumber ? (
                        <a
                          href={`/vlp/lab-orders/${row.orderNumber}/samples/${row.sampleNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1976d2",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {row.sampleNumber}
                        </a>
                      ) : (
                        "-"
                      )}
                    </TableCell>

                    <TableCell>{row.clientName || "-"}</TableCell>
                    <TableCell>{row.parameter || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.priority || "Normal"}
                        size="small"
                        sx={{
                          backgroundColor: getPriorityColor(row.priority),
                          color: "white",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: isOverdue(row.dueForAnalyst)
                          ? "#f44336"
                          : "inherit",
                      }}
                    >
                      {formatDate(row.dueForAnalyst)}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: isReportOverdue(row.reportDueDate)
                          ? "#ff9800"
                          : "inherit",
                      }}
                    >
                      {formatDate(row.reportDueDate)}
                    </TableCell>
                    <TableCell>
                      {row.methodCode ? (
                        <a
                          href={`/vlp/lab-orders/${row.orderNumber}/samples/${row.sampleNumber}/method-code/${row.methodCode}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1976d2",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {row.methodCode}
                        </a>
                      ) : (
                        "-"
                      )}
                    </TableCell>

                    <TableCell>{row.daysInHouse || 0}</TableCell>
                    <TableCell>{row.turnAroundTime || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}

      {!data?.length && (
        <Box mt={4} textAlign="center">
          <Typography variant="h6">📋 No Pending Analyst Data</Typography>
        </Box>
      )}
    </Box>
  );
}
